import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Alert, Empty, Spin } from 'antd';
import Map, { Layer, Marker, Source } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { useMeasurementReport } from './measurementReport/MeasurementReportContext';
import { MeasurementSectionShell, buildMeasurementSectionMeta } from './measurementReport/MeasurementSectionShell';

interface CommonSectionProps {
    section: any;
    onPatternPointClick?: (patternPointId: number, patternId: number, areaId: number) => void;
}

export const ReportMeasurementPatternLocation: React.FC<CommonSectionProps> = ({ section, onPatternPointClick }) => {
    const {
        measurementId,
        measurement,
        loading,
        patternPoints,
        patternPointsLoading,
        areaGeometryLoading,
        areaGeometry,
        areaType,
    } = useMeasurementReport();
    const sectionTitle = section?.title || 'Pattern location';
    const sectionMeta = buildMeasurementSectionMeta(measurement);

    const [imageBounds, setImageBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const [templateImageFailed, setTemplateImageFailed] = useState(false);
    const previewRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const mapRef = useRef<MapRef>(null);

    const MAPBOX_TOKEN =
        process.env.REACT_APP_MAPBOX_TOKEN ||
        'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';

    const templateImageUrl = useMemo(() => {
        if (areaType?.id && areaType?.templateImage) {
            return `${ENDPOINTS.AREA_TYPES_V2}/${areaType.id}/template-image?t=${Date.now()}`;
        }
        return undefined;
    }, [areaType]);

    const templateLinesUrl = useMemo(() => {
        if (areaType?.id && areaType?.templateLinesImage) {
            return `${ENDPOINTS.AREA_TYPES_V2}/${areaType.id}/template-lines-image?t=${Date.now()}`;
        }
        return undefined;
    }, [areaType]);

    useEffect(() => {
        setTemplateImageFailed(false);
    }, [templateImageUrl]);

    const templateBackgroundUrl = useMemo(() => {
        if (templateImageUrl && !templateImageFailed) {
            return templateImageUrl;
        }
        return templateLinesUrl;
    }, [templateImageUrl, templateImageFailed, templateLinesUrl]);

    const templateDimensions = useMemo(() => {
        const dims = areaType?.templateDimensions || null;
        if (!dims) return null;
        const width = Number((dims as any).widthMeters ?? (dims as any).width);
        const height = Number((dims as any).heightMeters ?? (dims as any).height);
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
            return null;
        }
        return { width, height };
    }, [areaType]);

    const resolvedPoints = useMemo(() => {
        return (patternPoints || [])
            .map((point: any) => {
                const rawX = typeof point.xLocal === 'number' ? point.xLocal : Number(point.xLocal);
                const rawY = typeof point.yLocal === 'number' ? point.yLocal : Number(point.yLocal);
                if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;
                if (rawX >= 0 && rawX <= 1 && rawY >= 0 && rawY <= 1) {
                    return { ...point, xLocal: rawX, yLocal: rawY };
                }
                if (templateDimensions) {
                    return {
                        ...point,
                        xLocal: rawX / templateDimensions.width,
                        yLocal: rawY / templateDimensions.height,
                    };
                }
                return null;
            })
            .filter(Boolean) as Array<{ id: number; xLocal: number; yLocal: number; label?: string | null; pointIndex?: number }>;
    }, [patternPoints, templateDimensions]);

    const measurementLocalPoint = useMemo(() => {
        const rawX = Number(measurement?.localPose?.x);
        const rawY = Number(measurement?.localPose?.y);
        if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return null;
        if (rawX >= 0 && rawX <= 1 && rawY >= 0 && rawY <= 1) {
            return { x: rawX, y: rawY };
        }
        if (templateDimensions) {
            return {
                x: rawX / templateDimensions.width,
                y: rawY / templateDimensions.height,
            };
        }
        return null;
    }, [measurement?.localPose, templateDimensions]);

    const resolvedAreaId = measurement?.area?.id || (measurement as any)?.areaId || null;
    const resolvedPatternId = measurement?.pattern?.id || (measurement as any)?.patternId || null;
    const isGpsPattern = measurement?.pattern?.coordSystem
        ? measurement.pattern.coordSystem.toLowerCase() !== 'local'
        : false;
    const showTemplateView = Boolean(templateBackgroundUrl) && resolvedPoints.length > 0 && !isGpsPattern;
    const showGpsView = !showTemplateView;

    const gpsPoints = useMemo(() => {
        return (patternPoints || [])
            .map((point: any) => {
                const gpsLat = typeof point.gpsLat === 'number' ? point.gpsLat : Number(point.gpsLat);
                const gpsLon = typeof point.gpsLon === 'number' ? point.gpsLon : Number(point.gpsLon);
                if (!Number.isFinite(gpsLat) || !Number.isFinite(gpsLon)) return null;
                return { ...point, gpsLat, gpsLon };
            })
            .filter(Boolean) as Array<{ id: number; gpsLat: number; gpsLon: number; label?: string | null; pointIndex?: number }>;
    }, [patternPoints]);

    const flattenCoords = useCallback((geom: any): [number, number][] => {
        if (!geom || !geom.type || !geom.coordinates) return [];
        if (geom.type === 'Polygon') {
            return (geom.coordinates as number[][][]).flat() as [number, number][];
        }
        if (geom.type === 'MultiPolygon') {
            return (geom.coordinates as number[][][][]).flat(2) as [number, number][];
        }
        return [];
    }, []);

    const fitMapToBounds = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        const coords = flattenCoords(areaGeometry);
        if (coords.length) {
            const lngs = coords.map((c) => c[0]);
            const lats = coords.map((c) => c[1]);
            if (lngs.length && lats.length) {
                map.fitBounds(
                    [
                        [Math.min(...lngs), Math.min(...lats)],
                        [Math.max(...lngs), Math.max(...lats)],
                    ],
                    { padding: 40, duration: 800 }
                );
                return;
            }
        }
        if (gpsPoints.length) {
            const lngs = gpsPoints.map((p) => p.gpsLon);
            const lats = gpsPoints.map((p) => p.gpsLat);
            map.fitBounds(
                [
                    [Math.min(...lngs), Math.min(...lats)],
                    [Math.max(...lngs), Math.max(...lats)],
                ],
                { padding: 40, duration: 800 }
            );
        }
    }, [areaGeometry, gpsPoints, flattenCoords]);

    useEffect(() => {
        if (!showGpsView) return;
        fitMapToBounds();
    }, [showGpsView, fitMapToBounds]);

    useEffect(() => {
        const calculateBoundsFromContainer = () => {
            const container = previewRef.current;
            if (!container) return;

            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            if (containerWidth === 0 || containerHeight === 0) {
                setTimeout(calculateBoundsFromContainer, 100);
                return;
            }

            if (!templateImageUrl) {
                setImageBounds({
                    left: 0,
                    top: 0,
                    width: containerWidth,
                    height: containerHeight,
                });
                return;
            }

            const image = imageRef.current;
            if (!image || !image.complete) return;

            const imageAspect = image.naturalWidth / image.naturalHeight;
            const containerAspect = containerWidth / containerHeight;
            let displayWidth;
            let displayHeight;
            let offsetX;
            let offsetY;

            if (containerAspect > imageAspect) {
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageAspect;
                offsetX = (containerWidth - displayWidth) / 2;
                offsetY = 0;
            } else {
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageAspect;
                offsetX = 0;
                offsetY = (containerHeight - displayHeight) / 2;
            }

            setImageBounds({
                left: offsetX,
                top: offsetY,
                width: displayWidth,
                height: displayHeight,
            });
        };

        if (templateBackgroundUrl) {
            const img = new Image();
            imageRef.current = img;
            img.onload = () => setTimeout(calculateBoundsFromContainer, 100);
            img.onerror = () => {
                if (templateImageUrl && templateBackgroundUrl === templateImageUrl) {
                    setTemplateImageFailed(true);
                }
                // Fallback to container bounds when image fails
                calculateBoundsFromContainer();
            };
            img.src = templateBackgroundUrl;
        } else {
            setTimeout(calculateBoundsFromContainer, 100);
        }

        const resizeObserver = new ResizeObserver(() => calculateBoundsFromContainer());
        if (previewRef.current) {
            resizeObserver.observe(previewRef.current);
        }

        return () => {
            resizeObserver.disconnect();
            imageRef.current = null;
        };
    }, [templateBackgroundUrl, templateImageUrl]);

    if (!measurementId) {
        return (
            <MeasurementSectionShell title={sectionTitle}>
                <Alert message="Select a measurement to view pattern location." type="info" showIcon />
            </MeasurementSectionShell>
        );
    }

    if (loading || patternPointsLoading || areaGeometryLoading) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Spin tip="Loading pattern location..." />
                </div>
            </MeasurementSectionShell>
        );
    }

    if (!measurement) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Empty description="Measurement not found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            </MeasurementSectionShell>
        );
    }

    if (!measurement?.pattern) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Empty description="Measurement is not assigned to a pattern" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            </MeasurementSectionShell>
        );
    }

    if (resolvedPoints.length === 0 && gpsPoints.length === 0) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Empty description="No pattern points available for this pattern" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            </MeasurementSectionShell>
        );
    }

    if (showGpsView && gpsPoints.length === 0) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Empty description="No GPS coordinates available for this pattern" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            </MeasurementSectionShell>
        );
    }

    return (
        <MeasurementSectionShell
            title={sectionTitle}
            meta={sectionMeta}
            toolbar={
                measurement.pattern ? (
                    <div className="report-measurement-summary">
                        <span className="report-measurement-pill">{measurement.pattern.name}</span>
                        {measurement.patternPoint ? (
                            <span className="report-measurement-pill report-measurement-pill--muted">
                                {measurement.patternPoint.label || `Point #${measurement.patternPoint.pointIndex}`}
                            </span>
                        ) : null}
                    </div>
                ) : undefined
            }
        >
        <div
            ref={previewRef}
            className="report-measurement-pattern-stage"
        >
            {showGpsView ? (
                <Map
                    ref={mapRef}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={{
                        longitude: gpsPoints[0]?.gpsLon ?? 0,
                        latitude: gpsPoints[0]?.gpsLat ?? 0,
                        zoom: 16,
                    }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                    onLoad={fitMapToBounds}
                >
                    {areaGeometry && (
                        <Source
                            id="measurement-area"
                            type="geojson"
                            data={{
                                type: 'Feature',
                                geometry: areaGeometry,
                                properties: {},
                            }}
                        >
                            <Layer id="measurement-area-fill" type="fill" paint={{ 'fill-color': '#2f54eb', 'fill-opacity': 0.1 }} />
                            <Layer id="measurement-area-outline" type="line" paint={{ 'line-color': '#1d39c4', 'line-width': 2 }} />
                        </Source>
                    )}
                    {gpsPoints.map((point) => {
                        const isSelected = point.id === measurement.patternPoint?.id;
                        const isClickable = !isSelected && Boolean(onPatternPointClick && resolvedPatternId && resolvedAreaId);
                        return (
                            <Marker key={point.id} longitude={point.gpsLon} latitude={point.gpsLat} anchor="center">
                                <div
                                    title={point.label || `Point ${point.pointIndex ?? point.id}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isClickable || !onPatternPointClick || !resolvedPatternId || !resolvedAreaId) return;
                                        onPatternPointClick(point.id, resolvedPatternId, resolvedAreaId);
                                    }}
                                    style={{
                                        width: isSelected ? 18 : 12,
                                        height: isSelected ? 18 : 12,
                                        borderRadius: '50%',
                                        backgroundColor: isSelected ? '#f5222d' : '#bfbfbf',
                                        border: isSelected ? '2px solid #ffffff' : '1px solid #ffffff',
                                        boxShadow: isSelected ? '0 0 8px rgba(245, 34, 45, 0.6)' : '0 2px 4px rgba(0,0,0,0.15)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        cursor: isClickable ? 'pointer' : 'default',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (isClickable) {
                                            e.currentTarget.style.transform = 'scale(1.3)';
                                            e.currentTarget.style.boxShadow = '0 0 8px rgba(245, 34, 45, 0.6)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.boxShadow = isSelected
                                            ? '0 0 8px rgba(245, 34, 45, 0.6)'
                                            : '0 2px 4px rgba(0,0,0,0.15)';
                                    }}
                                />
                            </Marker>
                        );
                    })}
                </Map>
            ) : (
                <>
                    {templateBackgroundUrl && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundImage: `url(${templateBackgroundUrl})`,
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                opacity: 0.6,
                                pointerEvents: 'none',
                            }}
                        />
                    )}
                    {imageBounds.width > 0 &&
                        resolvedPoints.map((point) => {
                            const isSelected = point.id === measurement.patternPoint?.id;
                            const isClickable = !isSelected && Boolean(onPatternPointClick && resolvedPatternId && resolvedAreaId);
                            return (
                                <div
                                    key={point.id}
                                    title={point.label || `Point ${point.pointIndex ?? point.id}`}
                                    onClick={() => {
                                        if (!isClickable || !onPatternPointClick || !resolvedPatternId || !resolvedAreaId) return;
                                        onPatternPointClick(point.id, resolvedPatternId, resolvedAreaId);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        left: `${imageBounds.left + point.xLocal * imageBounds.width}px`,
                                        top: `${imageBounds.top + (1 - point.yLocal) * imageBounds.height}px`,
                                        transform: 'translate(-50%, -50%)',
                                        width: isSelected ? 18 : 12,
                                        height: isSelected ? 18 : 12,
                                        borderRadius: '50%',
                                        backgroundColor: isSelected ? '#f5222d' : '#bfbfbf',
                                        border: isSelected ? '2px solid #ffffff' : '1px solid #ffffff',
                                        boxShadow: isSelected ? '0 0 8px rgba(245, 34, 45, 0.6)' : '0 2px 4px rgba(0,0,0,0.15)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        cursor: isClickable ? 'pointer' : 'default',
                                    }}
                                />
                            );
                        })}
                    {imageBounds.width > 0 && measurementLocalPoint ? (
                        <div
                            title="Measurement local position"
                            style={{
                                position: 'absolute',
                                left: `${imageBounds.left + measurementLocalPoint.x * imageBounds.width}px`,
                                top: `${imageBounds.top + (1 - measurementLocalPoint.y) * imageBounds.height}px`,
                                transform: 'translate(-50%, -50%)',
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                backgroundColor: '#1677ff',
                                border: '3px solid #ffffff',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                zIndex: 12,
                            }}
                        />
                    ) : null}
                </>
            )}
        </div>
        </MeasurementSectionShell>
    );
};

export default ReportMeasurementPatternLocation;
